import { ETextStyleVariant, Text } from '@outdoorsyco/bonfire';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { usePopper } from 'react-popper';

import { useExperimentIsEnabled } from '@/services/experiments';
import { OptimizelyFlags } from '@/services/experiments/flags';
import { getItemFromLocalStorage, setItemInLocalStorage } from '@/utility/localstorage';

import css from './SearchModesPopover.module.css';

const STORAGE_KEY = 'search-modes-popover-shown';

interface SearchModesPopoverProps {
  containerRef?: React.RefObject<HTMLElement>;
  // Hide popover when user opens location, guests, or dates tabs on desktop
  // or when "Trip information" modal is opened on mobile
  shouldHideTemporarily?: boolean;
  // Control whether this popover should show on mobile or desktop
  showOnMobile?: boolean;
}

export const SearchModesPopover = ({
  containerRef,
  shouldHideTemporarily,
  showOnMobile = false,
}: SearchModesPopoverProps) => {
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const [showNotification, setShowNotification] = useState(false);
  const [wasVisibleBeforeHiding, setWasVisibleBeforeHiding] = useState(false);

  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );

  const popperOptions = {
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement,
          padding: 10,
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom-start'], // Only allow bottom variations
          allowedAutoPlacements: ['bottom-start'], // Only allow bottom placements
        },
      },
      {
        name: 'preventOverflow',
        options: {
          mainAxis: false,
          altAxis: false,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [8, 8], // Add some space between the button and popover
        },
      },
      {
        name: 'computeStyles',
        options: {
          // Disable GPU acceleration to prevent jumping
          gpuAcceleration: false,
          // Use absolute positioning instead of transform
          adaptive: false,
        },
      },
    ],
    placement: 'bottom-start' as const,
    strategy: 'fixed' as const, // Use fixed positioning for sticky headers
  };

  const { styles: popperStyles, attributes } = usePopper(
    containerRef?.current,
    popperElement,
    popperOptions,
  );

  // Show notification after a delay to allow layout to settle
  useEffect(() => {
    const hasBeenShown = Boolean(getItemFromLocalStorage(STORAGE_KEY));
    if (!hasBeenShown && isRentalsVDeliverySearchModeEnabled && containerRef?.current) {
      const timer = setTimeout(() => {
        setShowNotification(true);
        // Remove forceUpdate call to prevent jumping
      }, 200); // Reduced delay for smoother appearance

      return () => clearTimeout(timer);
    }
  }, [isRentalsVDeliverySearchModeEnabled, containerRef]);

  // Hide notification temporarily when other tabs/modals are open, but show again when closed
  useEffect(() => {
    if (shouldHideTemporarily) {
      // Track if the popover was visible before hiding
      if (showNotification) {
        setWasVisibleBeforeHiding(true);
      }
      setShowNotification(false);
    } else {
      // Show again if it was visible before hiding, or if it should be shown for the first time
      const hasBeenShown = Boolean(getItemFromLocalStorage(STORAGE_KEY));

      if (
        (wasVisibleBeforeHiding || !hasBeenShown) &&
        isRentalsVDeliverySearchModeEnabled &&
        containerRef?.current
      ) {
        // Small delay to allow for smooth transitions
        const timer = setTimeout(() => {
          setShowNotification(true);
          // Reset the tracking state
          setWasVisibleBeforeHiding(false);
          // Remove forceUpdate call to prevent jumping
        }, 100);
        return () => clearTimeout(timer);
      } else {
        // Reset tracking state even if not showing
        setWasVisibleBeforeHiding(false);
      }
    }
  }, [
    shouldHideTemporarily,
    isRentalsVDeliverySearchModeEnabled,
    containerRef,
    showNotification,
    wasVisibleBeforeHiding,
  ]);

  const handleClose = useCallback(() => {
    setShowNotification(false);
    setWasVisibleBeforeHiding(false);
    setItemInLocalStorage(STORAGE_KEY, 'true');
  }, []);

  // Early return if notification shouldn't be shown
  if (!showNotification || !isRentalsVDeliverySearchModeEnabled || !containerRef?.current)
    return null;

  return createPortal(
    <div
      ref={setPopperElement}
      style={{ ...popperStyles.popper, zIndex: 1300 }}
      className={showOnMobile ? 'block md:hidden' : 'hidden md:block'}
      data-testid="search-modes-popover"
      {...attributes.popper}>
      <div className="p-4 text-white bg-gray-800 rounded-lg w-fit max-w-[258px]">
        <div ref={setArrowElement} className={css.arrow} />
        <div className="flex items-center gap-2">
          <Text
            variant={ETextStyleVariant.Label}
            className="text-white rounded-lg p-[6px] bg-[#B95B46]">
            <FormattedMessage defaultMessage="NEW" id="5Jcaq4" />
          </Text>
          <Text variant={ETextStyleVariant.SmallRegular}>
            <FormattedMessage defaultMessage="Introducing Search Modes" id="U67CMH" />
          </Text>
        </div>
        <div>
          <Text className="py-2" variant={ETextStyleVariant.LegalRegular}>
            <FormattedMessage
              defaultMessage="You can now switch between drivable and stationary RVs right from the search bar."
              id="u7BjCP"
            />
          </Text>
        </div>
        <div>
          <Text
            variant={ETextStyleVariant.LegalLink}
            onClick={handleClose}
            className="cursor-pointer w-fit">
            <FormattedMessage defaultMessage="Got it" id="NYTGIb" />
          </Text>
        </div>
      </div>
    </div>,
    document.body,
  );
};
